import React, { createContext, useCallback, useContext, useEffect } from "react";
import Tenant from "./TenantContext.d";
import { addTenantToPath, countryCodeToTenantCode, getTenantFromPath, removeTenantFromPath, TenantCode, tenantCodeToCountryCode } from "../../utils/tenantUtil";
import { useHistory, useLocation } from "react-router-dom";
import { CountryCode } from "../../utils/CountryCode";
import useInitialAppData from "../../Hook/UseInitialAppData";
import { useUserProfile } from "../../Hook";

const TenantContext = createContext<Tenant | undefined>(undefined);

interface TenantProviderProps {
    children: React.ReactNode;
}

const TenantProvider: React.FC<TenantProviderProps> = ({children}) => {
    const location = useLocation();
    const initialAppData = useInitialAppData();
    const history = useHistory();
	const userProfile = useUserProfile();

    const userDefaultLocation = initialAppData.userInformation.country;

    const getCurrentTenant = useCallback((): TenantCode => {
        const urlTenant = getTenantFromPath(location.pathname);
		if (urlTenant) {
			return urlTenant;
		}

        return countryCodeToTenantCode(userDefaultLocation || CountryCode.AU);
    }, [location.pathname, userDefaultLocation]);

    const currentTenant = getCurrentTenant();
	const currentCountry = tenantCodeToCountryCode(currentTenant);

    const switchTenant = useCallback((newTenant: TenantCode) => {
		if (newTenant === currentTenant) return;
	
		// Update URL
		const currentPathWithoutTenant = removeTenantFromPath(location.pathname);
		const newPath = addTenantToPath(currentPathWithoutTenant, newTenant);
		// Preserve search parameters and hash when switching tenants
		const fullNewPath = newPath + location.search + location.hash;

		// Force page refresh to reload content with new tenant
		window.location.href = fullNewPath;
	}, [currentTenant, location.pathname, location.search, location.hash]);

	const switchCountry = useCallback((newCountry: CountryCode) => {
		const newTenant = countryCodeToTenantCode(newCountry);
		switchTenant(newTenant);
	}, [switchTenant]);

    useEffect(() => {
		const urlTenant = getTenantFromPath(location.pathname);

		if (!urlTenant && userProfile) {
			// No tenant in URL, redirect to include tenant
			const fallbackCountry = userDefaultLocation || CountryCode.AU;
			
			const tenantCode = countryCodeToTenantCode(fallbackCountry);
			const newPath = addTenantToPath(location.pathname, tenantCode);
			// Preserve search parameters and hash when redirecting
			const fullNewPath = newPath + location.search + location.hash;
			history.replace(fullNewPath);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.pathname, userProfile, history, userDefaultLocation]);

    const value: Tenant = {
		currentTenant,
        currentCountry,
		switchCountry
	};
    
	return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export default TenantProvider;

export const useTenant = (): Tenant => {
	const context = useContext(TenantContext);
	if (context === undefined) {
		throw new Error("useTenant must be used within a TenantProvider");
	}
	return context;
};